// Agent Types and Data for DUCKMON Agent Ecosystem
// This file contains the real agent definitions, NOT mock data

// ============================================
// AGENT ARCHITECTURE TYPES
// ============================================

export interface AIAgent {
    id: string;
    name: string;
    version: string;
    type: 'trading' | 'prediction' | 'analysis' | 'mev' | 'security' | 'monitoring';
    description: string;
    features: string[];
    indicators: string[];
    status: 'running' | 'stopped' | 'beta';
    updateInterval: number; // in seconds
    onChainEnabled: boolean;
    category?: 'core' | 'advanced' | 'experimental';
    icon?: string;
}

export interface AgentSignal {
    type: 'BUY' | 'SELL' | 'HOLD';
    confidence: number;
    price: number;
    reason: string;
    timestamp: number;
    agentId: string;
}

export interface AgentPrediction {
    direction: 'UP' | 'DOWN' | 'SIDEWAYS';
    confidence: number;
    horizon: number; // minutes
    targetPrice: number;
    currentPrice: number;
    timestamp: number;
    agentId: string;
}

export interface MarketAlert {
    level: 'CRITICAL' | 'WARNING' | 'INFO';
    type: 'WHALE' | 'VOLATILITY' | 'HEALTH' | 'TREND';
    message: string;
    timestamp: number;
}

// ============================================
// REAL AGENT DEFINITIONS
// ============================================

export const DUCKMON_AGENTS: AIAgent[] = [
    // Core Trading Agents (v3.0)
    {
        id: 'trading-oracle',
        name: 'Trading Oracle v3.0',
        version: '3.0.0',
        type: 'trading',
        category: 'core',
        icon: '🦆',
        description: 'Professional AI-powered trading signal generator with 8-indicator weighted scoring, Ichimoku Cloud, Stochastic RSI, and Gemini AI analysis.',
        features: [
            'AI-Enhanced Technical Analysis (Gemini 2.0)',
            '8-indicator weighted scoring (RSI, MACD, Bollinger, Ichimoku, StochRSI, Trend, Momentum, VWAP)',
            'Real-time blockchain signal posting',
            'Market regime detection & Fear/Greed Index',
        ],
        indicators: ['RSI', 'MACD', 'Bollinger Bands', 'Ichimoku Cloud', 'Stochastic RSI', 'VWAP', 'ATR', 'AI Sentiment'],
        status: 'running',
        updateInterval: 900,
        onChainEnabled: true,
    },
    {
        id: 'prediction-bot',
        name: 'Prediction Bot v3.0',
        version: '3.0.0',
        type: 'prediction',
        category: 'core',
        icon: '🔮',
        description: 'Ensemble model price prediction with 4 algorithmic components: Linear Regression, Weighted MA, Mean Reversion, and Momentum analysis.',
        features: [
            'Ensemble prediction model (4 components)',
            'Multi-horizon forecasts (5m, 15m, 1h, 4h)',
            'Confidence intervals with ATR-based width',
            'Rolling accuracy tracking & verification',
        ],
        indicators: ['Linear Regression', 'Weighted MA', 'Mean Reversion', 'Momentum', 'R-squared'],
        status: 'running',
        updateInterval: 900,
        onChainEnabled: true,
    },
    {
        id: 'market-analyzer',
        name: 'Market Analyzer v3.0',
        version: '3.0.0',
        type: 'analysis',
        category: 'core',
        icon: '📊',
        description: 'Advanced market intelligence with full technical analysis, AI-enhanced insights, market regime detection, and Fibonacci levels.',
        features: [
            'Full technical analysis (15+ indicators)',
            'AI-powered market analysis (Gemini)',
            'Market regime detection (Trending/Ranging/Volatile)',
            'Fibonacci retracement & support/resistance levels',
        ],
        indicators: ['Fear/Greed', 'Market Health', 'Ichimoku', 'Fibonacci', 'Bollinger %B', 'ATR'],
        status: 'running',
        updateInterval: 900,
        onChainEnabled: true,
    },
    {
        id: 'whale-observer',
        name: 'Whale Observer v2.0',
        version: '2.0.0',
        type: 'monitoring',
        category: 'core',
        icon: '🐋',
        description: 'Real-time whale tracking via Transfer event log scanning with AI behavior analysis and wallet profiling.',
        features: [
            'Transfer event log scanning (real whale discovery)',
            'AI-powered whale behavior analysis (Gemini)',
            'Wallet profiling (Accumulator, Distributor, Trader)',
            'Network health monitoring with proper gas tracking',
        ],
        indicators: ['Transfer Logs', 'Wallet Profiles', 'Net Flow', 'AI Intent Analysis', 'Network Gas'],
        status: 'running',
        updateInterval: 300,
        onChainEnabled: true,
    },

    // New Web3 Agents
    {
        id: 'liquidity-sentinel',
        name: 'Liquidity Sentinel v1.0',
        version: '1.0.0',
        type: 'monitoring',
        category: 'core',
        icon: '💧',
        description: 'Monitors nad.fun bonding curve progress, LP health, and calculates rug pull risk scores with AI-enhanced analysis.',
        features: [
            'Bonding curve graduation tracking',
            'Liquidity health assessment',
            'Rug pull risk scoring (0-100)',
            'Price impact estimation & slippage alerts',
        ],
        indicators: ['Bonding Progress', 'LP Health', 'Rug Risk', 'Buy/Sell Ratio', 'Slippage'],
        status: 'running',
        updateInterval: 600,
        onChainEnabled: true,
    },
    {
        id: 'social-sentiment',
        name: 'Social Sentiment v1.0',
        version: '1.0.0',
        type: 'analysis',
        category: 'core',
        icon: '📢',
        description: 'Aggregates social signals from transaction data, volume trends, and buy/sell ratios to produce sentiment scores.',
        features: [
            'Multi-factor sentiment scoring (0-100)',
            'Buy/Sell ratio analysis (1h, 6h, 24h)',
            'Volume trend acceleration detection',
            'Transaction activity profiling',
        ],
        indicators: ['Sentiment Score', 'Buy/Sell Ratio', 'Volume Trend', 'TX Activity', 'Momentum'],
        status: 'running',
        updateInterval: 900,
        onChainEnabled: true,
    },
    {
        id: 'onchain-analytics',
        name: 'On-Chain Analytics v1.0',
        version: '1.0.0',
        type: 'analysis',
        category: 'core',
        icon: '🔗',
        description: 'Deep on-chain analysis with holder tracking, organic volume detection, and wash trading identification.',
        features: [
            'Holder growth tracking via Transfer events',
            'Buy/Sell flow classification',
            'Organic volume scoring (wash trade detection)',
            'Token velocity & concentration analysis',
        ],
        indicators: ['Holder Count', 'Buy/Sell Ratio', 'Organic Score', 'Token Velocity', 'Active Addresses'],
        status: 'running',
        updateInterval: 600,
        onChainEnabled: true,
    },

    // Advanced MEV & DeFi Agents
    {
        id: 'mev-bot',
        name: 'MEV Bot v1.0',
        version: '1.0.0',
        type: 'mev',
        category: 'advanced',
        icon: '⚡',
        description: 'Maximal Extractable Value bot for arbitrage, backrun, and liquidation opportunities. Leverages Monad\'s 10K TPS.',
        features: [
            'Cross-DEX arbitrage detection',
            'Backrun opportunity hunting',
            'Liquidation sniping',
            'Risk management & slippage protection',
        ],
        indicators: ['Price Spreads', 'Gas Optimization', 'Liquidity Depth', 'Profit Estimation'],
        status: 'beta',
        updateInterval: 1,
        onChainEnabled: false,
    },
    {
        id: 'token-launch-detector',
        name: 'Token Launch Detector v1.0',
        version: '1.0.0',
        type: 'security',
        category: 'advanced',
        icon: '🚀',
        description: 'Automated new token discovery with safety scoring and honeypot detection. Scans every block for new pairs.',
        features: [
            'Real-time new token scanning',
            'Safety score (0-100)',
            'Honeypot detection',
            'Liquidity & ownership verification',
        ],
        indicators: ['Contract Analysis', 'Ownership %', 'Liquidity Lock', 'Sell Tax'],
        status: 'beta',
        updateInterval: 5,
        onChainEnabled: false,
    },
    {
        id: 'gas-optimizer',
        name: 'Gas Optimizer v1.0',
        version: '1.0.0',
        type: 'monitoring',
        category: 'advanced',
        icon: '⛽',
        description: 'Real-time gas price monitoring with predictions. Tells you the optimal time to trade.',
        features: [
            'Real-time gas tracking',
            'Price predictions',
            'Optimal trade timing',
            'Historical volatility analysis',
        ],
        indicators: ['Current Gas', 'Avg Gas', 'Volatility', 'Network Congestion'],
        status: 'running',
        updateInterval: 10,
        onChainEnabled: false,
    },
];

// ============================================
// AGENT ECOSYSTEM ARCHITECTURE
// ============================================

export const ECOSYSTEM_ARCHITECTURE = {
    dataFlow: [
        {
            step: 1,
            title: 'Price Feed',
            description: 'Agents fetch real-time DUCK price from Nad.fun Lens contract on Monad',
            icon: '📡',
        },
        {
            step: 2,
            title: 'Analysis',
            description: 'Each agent applies its specialized algorithms (TA, Neural Network, Market Intel)',
            icon: '🧠',
        },
        {
            step: 3,
            title: 'Signal Generation',
            description: 'Agents generate signals/predictions based on their analysis',
            icon: '⚡',
        },
        {
            step: 4,
            title: 'On-Chain Storage',
            description: 'High-confidence signals are written to DuckSignals smart contract',
            icon: '⛓️',
        },
        {
            step: 5,
            title: 'Frontend Display',
            description: 'Website reads from blockchain and displays live signals',
            icon: '🖥️',
        },
    ],
    contracts: [
        {
            name: 'DuckSignals.sol',
            address: 'Deployed on Monad Mainnet',
            functions: ['registerAgent', 'postSignal', 'postPrediction', 'getRecentSignals'],
        },
        {
            name: 'Nad.fun Lens',
            address: '0x7e78A8DE94f21804F7a17F4E8BF9EC2c872187ea',
            functions: ['getAmountOut', 'isGraduated', 'getProgress'],
        },
    ],
    technology: {
        agents: ['Node.js', 'viem', 'Gemini AI', '7 Autonomous Agents'],
        frontend: ['React', 'wagmi', 'TypeScript', 'recharts'],
        blockchain: ['Monad (EVM)', 'Solidity 0.8.20', 'DuckSignals Contract'],
    },
};

// ============================================
// (Demo data removed - all data comes from on-chain DuckSignals contract)
// ============================================

// ============================================
// TOURNAMENT TYPES (Future Feature)
// ============================================

export enum TournamentStatus {
    Open = 0,
    Active = 1,
    Completed = 2,
    Cancelled = 3
}

export interface Tournament {
    id: string;
    name: string;
    entryFee: number;
    prizePool: number;
    participants: number;
    maxParticipants: number;
    startTime: number;
    status: TournamentStatus;
    gameType: 'trading' | 'prediction' | 'strategy';
}

// Tournaments will be populated from on-chain DAP Arena contract when deployed
